/**
 * zReport — ESC/POS Z-report builder for Day-Close.
 *
 * Independent of the invoice receipt pipeline: builds a plain-text
 * summary and converts it to ESC/POS bytes using the low-level engine.
 *
 * Can also be called as a standalone async function (no React hooks)
 * so it is easy to trigger from DayCloseScreen after closeSession().
 */

import { useSettingsStore } from '@/store/useSettingsStore';
import { printer } from '@/platform';
import { escPos } from './engine/EscPosCommands';
import { buildReceiptHtml } from './templates/receiptHtml';
import { centerText, hr, padLeft, padRight, twoCol } from './engine/LineFormatter';
import { formatInr } from './engine/NumberFormatters';
import type { PaperWidth, PrintResult } from './contracts/PrintTypes';

export interface ZReportData {
  storeName: string;
  storeAddress?: string;
  reportDate: string;      // "DD/MM/YYYY"
  reportTime: string;      // "HH:MM"
  openingCash: number;
  cashSales: number;
  upiSales: number;
  cardSales: number;
  otherSales: number;
  creditDue: number;
  totalSales: number;
  invoiceCount: number;
  cashExpenses: number;
  expectedCash: number;
  actualCash: number;
  variance: number;
}

function buildZLines(data: ZReportData, w: number): string[] {
  const lines: string[] = [];

  lines.push(hr(w, '='));
  lines.push(centerText(data.storeName, w));
  if (data.storeAddress) lines.push(centerText(data.storeAddress, w));
  lines.push(hr(w, '='));
  lines.push(centerText('Z-REPORT / END OF DAY', w));
  lines.push(twoCol('Date:', data.reportDate, w));
  lines.push(twoCol('Time:', data.reportTime, w));
  lines.push(hr(w, '-'));

  lines.push(twoCol('Total Sales:', formatInr(data.totalSales), w));
  lines.push(twoCol('No. of Invoices:', String(data.invoiceCount), w));
  lines.push(hr(w, '-'));

  lines.push(' PAYMENT BREAKDOWN');
  lines.push(twoCol('  Cash:', formatInr(data.cashSales), w));
  lines.push(twoCol('  UPI:', formatInr(data.upiSales), w));
  lines.push(twoCol('  Card:', formatInr(data.cardSales), w));
  if (data.otherSales > 0)
    lines.push(twoCol('  Other:', formatInr(data.otherSales), w));
  if (data.creditDue > 0)
    lines.push(twoCol('  Credit Due:', formatInr(data.creditDue), w));
  lines.push(hr(w, '-'));

  lines.push(' CASH RECONCILIATION');
  lines.push(twoCol('  Opening Cash:', formatInr(data.openingCash), w));
  if (data.cashExpenses > 0)
    lines.push(twoCol('  Cash Expenses:', `-${formatInr(data.cashExpenses)}`, w));
  lines.push(twoCol('  Expected Cash:', formatInr(data.expectedCash), w));
  lines.push(twoCol('  Actual Cash:', formatInr(data.actualCash), w));
  const varianceLabel = data.variance >= 0 ? '  Overage:' : '  Short:';
  lines.push(twoCol(varianceLabel, formatInr(Math.abs(data.variance)), w));
  lines.push(hr(w, '='));
  lines.push(centerText('--- END OF DAY REPORT ---', w));
  lines.push(hr(w, '='));

  return lines;
}

/**
 * Build the raw ESC/POS bytes for the Z-report. Pure, no I/O.
 */
export function buildZReportBytes(data: ZReportData, paperWidth: PaperWidth = 80, autoCut = true): Uint8Array {
  const w = paperWidth === 58 ? 32 : paperWidth === 112 ? 56 : 42;
  const lines = buildZLines(data, w);

  return escPos.concat(
    escPos.init(),
    escPos.alignLeft(),
    ...lines.map((line) => escPos.textLine(line)),
    escPos.feed(3),
    autoCut ? escPos.cut() : new Uint8Array(),
  );
}

/**
 * Print the Z-report through the browser's print dialog, on the receipt roll.
 *
 * Desktop sends these bytes to a network printer over TCP; a browser cannot,
 * so on the web this used to fail with "Thermal printing is disabled" however
 * the till was set up. The same lines print as a roll-width page instead.
 */
export async function printZReport(data: ZReportData): Promise<PrintResult> {
  const s = useSettingsStore.getState();
  const rawWidth = s.getNumber('THERMAL_PRINTER_PAPER_WIDTH');
  const paperWidth = (rawWidth === 58 || rawWidth === 112 ? rawWidth : 80) as PaperWidth;
  const w = paperWidth === 58 ? 32 : paperWidth === 112 ? 56 : 42;
  try {
    const res = await printer.printHtml({ html: buildReceiptHtml(buildZLines(data, w).join('\n'), paperWidth) });
    return { ok: res.ok, transport: 'system', errorCode: res.ok ? undefined : 'unknown', message: res.ok ? 'Sent to printer' : res.message || 'Print failed' };
  } catch (err) {
    return { ok: false, transport: 'system', errorCode: 'unknown', message: (err as Error).message };
  }
}
